import React, { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, Search } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { getMerchantTypeLabel, MerchantType } from '@/components/admin/MerchantTypeSelector';
import { useMerchantStatusFilter } from '@/hooks/useMerchantStatusFilter';
import {
  MerchantStatusId,
  deriveMerchantStatus,
  stripTestNamePrefix,
} from '@/shared/merchantStatus';
import { MerchantStatusBadge } from './MerchantStatusBadge';
import { MerchantStatusFilter } from './MerchantStatusFilter';
import { MerchantsSummaryTiles, type MerchantsGmvSummary } from './MerchantsSummaryTiles';
import {
  compareGmv,
  describeGmvCutoff,
  gmvValue,
  sumGmv,
  type GmvColumn,
  type MerchantGmvFigures,
} from '@/shared/merchantGmv';
import { formatWholeDollars } from '@/shared/salesHistory';
import type { MerchantGmvData } from '@/hooks/useMerchantGmv';

// Admin Dashboard › Merchants list (FIG-318). Type and Status are read-only
// here; both are set on the merchant's Settings tab (FIG-320). GMV columns and
// tiles come from the nightly ∑: Variants totals (FIG-345 · UC-230).

export interface MerchantListItem {
  id: string;
  name: string;
  poc_name: string | null;
  poc_email: string | null;
  merchant_type: string | null;
  status?: string | null;
  is_test_clone?: boolean | null;
}

interface MerchantsListProps<T extends MerchantListItem> {
  merchants: T[];
  onOpen: (merchant: T) => void;
  /** Rendered to the right of the summary tiles (e.g. the add button). */
  headerAction?: React.ReactNode;
  /** Temporary per-row actions until they move to the Settings tab (FIG-320, REQ-038). */
  renderActions?: (merchant: T) => React.ReactNode;
  /** Nightly GMV figures (merchant-gmv-v1). Omitted: the GMV cells show "—". */
  gmv?: { state: 'loading' | 'error' | 'ready'; data?: MerchantGmvData | null };
}

type SortDirection = 'asc' | 'desc';
type SortColumn = 'name' | GmvColumn;

const GMV_LABELS: Record<GmvColumn, string> = { gmvAllTime: 'GMV (All Time)', gmvT12m: 'GMV (Trailing 12M)' };

/** Source and cut-off, for the header tooltips, the tile ⓘ and the footer. */
export const gmvSourceNote = (asOf: string) =>
  `∑: Variants report total GMV, with each merchant's saved Variants source, to ${describeGmvCutoff(asOf)}.`;

const sortName = (name: string) => stripTestNamePrefix(name).toLowerCase();

const initialOf = (name: string) => (stripTestNamePrefix(name).match(/[a-zA-Z]/) || ['?'])[0].toUpperCase();

const GmvSortHeader: React.FC<{
  column: GmvColumn;
  active: boolean;
  direction: SortDirection;
  note: string | null;
  onSort: () => void;
}> = ({ column, active, direction, note, onSort }) => {
  const label = GMV_LABELS[column];
  const Icon = !active ? ArrowUpDown : direction === 'asc' ? ArrowUp : ArrowDown;
  const next = active && direction === 'desc' ? 'lowest first' : 'highest first';
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5 px-2 font-medium text-foreground"
            onClick={onSort}
            aria-label={`Sort by ${label}, ${next}`}
          >
            {label}
            <Icon className="h-3 w-3" aria-hidden="true" />
          </Button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <p>{note ?? 'GMV has not been calculated yet.'} Merchants with no data are listed last.</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const GmvCell: React.FC<{ figures: MerchantGmvFigures | undefined; column: GmvColumn; latestAsOf: string | null }> = ({ figures, column, latestAsOf }) => {
  if (!figures) {
    return <TableCell className="text-right text-muted-foreground" aria-label="Not calculated yet">—</TableCell>;
  }
  const value = gmvValue(figures, column);
  // A merchant whose last night failed keeps its previous figures, with their own cut-off.
  const olderCutoff = latestAsOf && figures.asOf !== latestAsOf ? `As of ${describeGmvCutoff(figures.asOf)}` : undefined;
  return (
    <TableCell className={value === null ? 'text-right text-muted-foreground' : 'text-right tabular-nums'} title={olderCutoff}>
      {value === null ? 'No data' : formatWholeDollars(value)}
    </TableCell>
  );
};

export function MerchantsList<T extends MerchantListItem>({
  merchants,
  onOpen,
  headerAction,
  renderActions,
  gmv,
}: MerchantsListProps<T>) {
  const filter = useMerchantStatusFilter();
  const [search, setSearch] = useState('');
  // Opens A–Z by name; the sort is not remembered (REQ-019).
  const [sortColumn, setSortColumn] = useState<SortColumn>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const gmvData = gmv?.state === 'ready' ? gmv.data ?? null : null;
  const latestAsOf = gmvData?.latestAsOf ?? null;
  const gmvNote = latestAsOf ? gmvSourceNote(latestAsOf) : null;
  const figuresFor = (id: string) => gmvData?.byOrganisation.get(id);

  const sortBy = (column: SortColumn) => {
    if (column === sortColumn) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      // Name opens A–Z; GMV opens highest first.
      setSortDirection(column === 'name' ? 'asc' : 'desc');
    }
  };

  const withStatus = useMemo(
    () => merchants.map((merchant) => ({ merchant, status: deriveMerchantStatus(merchant) })),
    [merchants],
  );

  const counts = useMemo(() => {
    const result: Partial<Record<MerchantStatusId, number>> = {};
    withStatus.forEach(({ status }) => {
      result[status] = (result[status] ?? 0) + 1;
    });
    return result;
  }, [withStatus]);

  const query = search.trim().toLowerCase();
  const { rows, hiddenCount } = useMemo(() => {
    const passingFilter = withStatus.filter(({ status }) => filter.selected.has(status));
    const visible = passingFilter
      .filter(({ merchant }) => !query
        || `${merchant.name} ${merchant.poc_name ?? ''} ${merchant.poc_email ?? ''}`.toLowerCase().includes(query))
      .sort((a, b) => {
        const byName = sortName(a.merchant.name).localeCompare(sortName(b.merchant.name));
        if (sortColumn === 'name') return sortDirection === 'asc' ? byName : -byName;
        const byGmv = compareGmv(
          gmvValue(gmvData?.byOrganisation.get(a.merchant.id), sortColumn),
          gmvValue(gmvData?.byOrganisation.get(b.merchant.id), sortColumn),
          sortDirection,
        );
        return byGmv !== 0 ? byGmv : byName;
      });
    return { rows: visible, hiddenCount: withStatus.length - passingFilter.length };
  }, [withStatus, filter.selected, query, sortColumn, sortDirection, gmvData]);

  const gmvSummary = useMemo<MerchantsGmvSummary | undefined>(() => {
    if (!gmv) return undefined;
    const totalsFor = (column: GmvColumn) => ({
      shown: sumGmv(rows.map(({ merchant }) => gmvValue(gmvData?.byOrganisation.get(merchant.id), column))),
      all: sumGmv(merchants.map((merchant) => gmvValue(gmvData?.byOrganisation.get(merchant.id), column))),
    });
    return { state: gmv.state, allTime: totalsFor('gmvAllTime'), t12m: totalsFor('gmvT12m'), sourceNote: gmvNote };
  }, [gmv, gmvData, gmvNote, rows, merchants]);

  const handleRowKeyDown = (event: React.KeyboardEvent<HTMLTableRowElement>, merchant: T) => {
    // Keys pressed on controls inside the row (actions) must not open it.
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onOpen(merchant);
    }
  };

  const clearAll = () => {
    setSearch('');
    filter.selectAll();
  };

  const columnCount = renderActions ? 7 : 6;
  const nameActive = sortColumn === 'name';
  const SortIcon = !nameActive ? ArrowUpDown : sortDirection === 'asc' ? ArrowUp : ArrowDown;
  const ariaSort = (column: SortColumn) =>
    sortColumn === column ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none';

  return (
    <div className="space-y-4">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <MerchantsSummaryTiles shownCount={rows.length} totalCount={merchants.length} gmv={gmvSummary} />
        {headerAction}
      </div>

      <section className="rounded-md bg-white shadow" aria-labelledby="merchants-heading">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b p-4">
          <h2 id="merchants-heading" className="text-xl font-semibold">Merchants</h2>
          <div className="flex flex-wrap items-center gap-2">
            <MerchantStatusFilter selected={filter.selected} counts={counts} onChange={filter.setSelected} />
            {filter.isChanged && (
              <Button variant="link" className="h-10 px-2" onClick={filter.reset}>
                Reset
              </Button>
            )}
            <div className="relative w-64 max-w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} aria-hidden="true" />
              <Input
                type="search"
                aria-label="Search merchants"
                placeholder="Search by name or contact..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead aria-sort={ariaSort('name')}>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="gap-1.5 px-2 font-medium text-foreground"
                    onClick={() => sortBy('name')}
                    aria-label={`Sort by name, ${nameActive && sortDirection === 'asc' ? 'Z to A' : 'A to Z'}`}
                  >
                    Name
                    <SortIcon className="h-3 w-3" aria-hidden="true" />
                  </Button>
                </TableHead>
                <TableHead>Contact</TableHead>
                {(['gmvAllTime', 'gmvT12m'] as GmvColumn[]).map((column) => (
                  <TableHead key={column} className="text-right" aria-sort={ariaSort(column)}>
                    <GmvSortHeader
                      column={column}
                      active={sortColumn === column}
                      direction={sortDirection}
                      note={gmvNote}
                      onSort={() => sortBy(column)}
                    />
                  </TableHead>
                ))}
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
                {renderActions && <TableHead className="w-[150px]">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length > 0 ? (
                rows.map(({ merchant, status }) => (
                  <TableRow
                    key={merchant.id}
                    data-testid={`org-${merchant.id}`}
                    // Keeps its implicit row role so table navigation still works;
                    // the row is focusable and opens on Enter or Space.
                    tabIndex={0}
                    aria-label={`Open ${merchant.name}`}
                    className="cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
                    onClick={() => onOpen(merchant)}
                    onKeyDown={(event) => handleRowKeyDown(event, merchant)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar aria-hidden="true">
                          <AvatarFallback>{initialOf(merchant.name)}</AvatarFallback>
                        </Avatar>
                        <span className="font-medium">{merchant.name}</span>
                        {merchant.is_test_clone && (
                          <Badge variant="outline" className="border-amber-300 bg-amber-100 text-xs text-amber-800">TEST</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>{merchant.poc_name}</div>
                      <div className="text-sm text-muted-foreground">{merchant.poc_email}</div>
                    </TableCell>
                    <GmvCell figures={figuresFor(merchant.id)} column="gmvAllTime" latestAsOf={latestAsOf} />
                    <GmvCell figures={figuresFor(merchant.id)} column="gmvT12m" latestAsOf={latestAsOf} />
                    <TableCell>{getMerchantTypeLabel((merchant.merchant_type || 'direct') as MerchantType)}</TableCell>
                    <TableCell><MerchantStatusBadge status={status} /></TableCell>
                    {renderActions && <TableCell className="text-right">{renderActions(merchant)}</TableCell>}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columnCount} className="py-12 text-center">
                    <div className="text-sm font-medium">No merchants match these filters</div>
                    <div className="mt-1 text-muted-foreground">Try another search or include more statuses.</div>
                    <Button variant="outline" size="sm" className="mt-4" onClick={clearAll}>
                      Clear filters
                    </Button>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t px-4 py-3 text-sm text-muted-foreground">
          <span data-testid="merchants-footer-count">Showing {rows.length} of {merchants.length} merchants</span>
          {gmv && (
            <span data-testid="merchants-gmv-note">
              {gmv.state === 'loading' && 'Loading GMV…'}
              {gmv.state === 'error' && "GMV couldn't be loaded."}
              {gmv.state === 'ready' && (gmvNote ? `GMV: ${gmvNote}` : 'GMV has not been calculated yet.')}
            </span>
          )}
          {hiddenCount > 0 && (
            <span>
              {hiddenCount} {hiddenCount === 1 ? 'merchant' : 'merchants'} hidden by the Status filter.
              <Button variant="link" className="h-auto p-0 pl-1" onClick={filter.selectAll}>
                View all
              </Button>
            </span>
          )}
        </div>
      </section>
    </div>
  );
}

export default MerchantsList;
